import { Check } from "lucide-react";
import { cn } from "@/utils/utils";
import Text from "@/components/ui/text";

export type StepperOrientation = "horizontal" | "vertical";

export type StepStatus = "done" | "current" | "next" | "disabled";

export interface StepperStep {
  id: string;
  title: string;
  counterLabel: string;
  disabled?: boolean;
}

export interface StepperProps {
  steps: StepperStep[];
  activeStep: number;
  orientation?: StepperOrientation;
  showStepCounter?: boolean;
  onStepClick?: (index: number, step: StepperStep) => void;
  className?: string;
}

const indicatorClasses: Record<StepStatus, string> = {
  done: "border-border-selected-button",
  current: "border-primary",
  next: "border-border-secondary-button",
  disabled: "border-border-disabled-button bg-buttons-cta-primary-disabled"
};

const getStepStatus = (
  index: number,
  activeStep: number,
  disabled?: boolean
): StepStatus => {
  if (disabled) return "disabled";
  if (index < activeStep) return "done";
  if (index === activeStep) return "current";
  return "next";
};

const StepIndicator = ({ status }: { status: StepStatus }) => (
  <span
    className={cn(
      "flex size-5 shrink-0 items-center justify-center rounded-full border transition-all",
      indicatorClasses[status],
      status === "disabled"
        ? ""
        : "group-hover:ring-[6px] group-hover:ring-surface-hover-soft group-focus-visible:ring-[6px] group-focus-visible:ring-surface-hover-soft"
    )}
  >
    {status === "done" ? (
      <Check className="size-3 text-text-body-tertiary" />
    ) : null}
    {status === "current" ? (
      <span className="size-2.5 rounded-full bg-primary" />
    ) : null}
  </span>
);

const StepCounter = ({
  step,
  status
}: {
  step: StepperStep;
  status: StepStatus;
}) => {
  return (
    <Text
      variant="smBodyTertiary"
      className={cn(
        "text-xs",
        status === "disabled" ? "text-text-disabled" : ""
      )}
    >
      {step.counterLabel}
    </Text>
  );
};

const Stepper = ({
  steps,
  activeStep,
  orientation = "horizontal",
  showStepCounter = true,
  onStepClick,
  className
}: StepperProps) => {
  const isVertical = orientation === "vertical";

  return (
    <ol
      className={cn(
        "flex",
        isVertical
          ? "w-full flex-col items-start justify-center"
          : "items-center",
        className
      )}
      data-test={`stepper-${orientation}`}
    >
      {steps.map((step, index) => {
        const status = getStepStatus(index, activeStep, step.disabled);
        const isClickable = Boolean(onStepClick) && status !== "disabled";
        const StepBox = isClickable ? "button" : "div";
        const counter = showStepCounter ? (
          <StepCounter step={step} status={status} />
        ) : null;

        return (
          <li
            key={step.id}
            className={cn(
              "relative min-w-[120px]",
              isVertical ? "w-full" : "flex flex-1"
            )}
          >
            <StepBox
              type={isClickable ? "button" : undefined}
              onClick={
                isClickable ? () => onStepClick?.(index, step) : undefined
              }
              aria-current={status === "current" ? "step" : undefined}
              aria-disabled={status === "disabled" ? true : undefined}
              className={cn(
                "group flex w-full gap-2.5 px-4 py-2 text-left",
                isVertical
                  ? "h-16 items-start"
                  : "flex-col items-start justify-center"
              )}
              data-test={`stepper-step-${step.id}`}
            >
              <StepIndicator status={status} />
              <div className="flex flex-col justify-center gap-1">
                {isVertical ? null : counter}
                <Text
                  variant="smBody"
                  className={cn(
                    "leading-5 group-hover:font-medium",
                    status === "disabled" ? "text-text-disabled" : ""
                  )}
                >
                  {step.title}
                </Text>
                {isVertical ? counter : null}
              </div>
            </StepBox>
            {index === steps.length - 1 ? null : (
              <span
                className={cn(
                  "absolute bg-border-border2",
                  isVertical
                    ? "bottom-0 left-[26px] top-9 w-px"
                    : "left-[67px] right-0 top-[17px] h-px"
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
};

export default Stepper;
